import {
  BodySmallRegular,
  BodyRegularBold
} from '@components/atoms/typography';
import { collectSimulatedProvenances } from '@lib/live/team/aggregation';
import { useTeamLive } from '@lib/live/team/context';

/**
 * The squad's running total, as the pitch itself computes it.
 *
 * This is `calculateLiveTotal`: points summed across every fixture a pick has,
 * with auto-substitutions and the vice-captaincy applied. It updates as match
 * state arrives, because it is derived from the same subscribed state the
 * tiles read, so it moves on the Ably feed rather than on a poll.
 *
 * Deliberately shown only for a simulated pitch. A real gameweek already has a
 * total on this page, `LiveScoreBoard`'s "GW points", which comes from the
 * live rank service and is net of transfer hits. The two are computed
 * differently and will legitimately disagree, so putting both in front of a
 * real user needs that divergence resolved first rather than shipped as a
 * surprise. A simulated pitch has no live rank data at all, which is why the
 * total is missing there and why this fills the gap.
 */
export const LiveTeamTotal = () => {
  const { phase, liveTotal, isLive, matchStates } = useTeamLive();

  const isSimulated = collectSimulatedProvenances(matchStates).length > 0;
  if (phase !== 'READY' || !isSimulated) return null;

  return (
    <div
      className="mt-4 flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-4 py-3"
      data-cy="live-team-total"
      data-testid="live-team-total"
    >
      <div className="flex flex-col">
        <BodySmallRegular textColor="text-neutral-500">
          Simulated running total
        </BodySmallRegular>
        <BodySmallRegular textColor="text-neutral-500">
          Auto-subs and the vice-captaincy applied
        </BodySmallRegular>
      </div>
      <div className="flex items-center gap-3">
        {isLive && (
          <span
            className="flex items-center gap-1.5 rounded-full bg-danger-50 px-2 py-1"
            data-cy="live-team-total-live"
            data-testid="live-team-total-live"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-danger-600" />
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-danger-600">
              Live
            </span>
          </span>
        )}
        <BodyRegularBold textColor="text-neutral-900">
          <span
            className="text-2xl tabular-nums"
            data-cy="live-team-total-points"
            data-testid="live-team-total-points"
          >
            {liveTotal}
          </span>{' '}
          <span className="text-xs font-medium uppercase tracking-[0.08em] text-neutral-400">
            pts
          </span>
        </BodyRegularBold>
      </div>
    </div>
  );
};
